import React, { useState, useEffect } from 'react';
import Cookies from 'js-cookie';

import PersonIcon from '@mui/icons-material/Person';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';

// API Base URL - build/フォルダがドキュメントルートとして機能している場合
const API_BASE = `https://system.sales-management.cloud/api/admin/report-field-config.php`;
const USERS_API = `https://system.sales-management.cloud/api/users.php`;

function ReportFieldConfig() {
    const [users, setUsers] = useState([]);
    const [selectedUserId, setSelectedUserId] = useState(null);
    const [fields, setFields] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [editingFieldId, setEditingFieldId] = useState(null);
    const [editingField, setEditingField] = useState(null);
    const [draggingIndex, setDraggingIndex] = useState(null);
    const [dragOverIndex, setDragOverIndex] = useState(null);
    const [newField, setNewField] = useState({
        field_name: '',
        field_type: 'text',
        field_order: 0,
        is_required: false,
        placeholder: '',
        options: [],
        optionsText: ''  // input 用の文字列
    });

    const fieldTypes = [
        { value: 'number', label: '数値' },
        { value: 'text', label: '単文' },
        { value: 'textarea', label: '長文' },
        { value: 'date', label: '日付' },
        { value: 'time', label: '時間' },
        { value: 'select', label: '選択肢' }
    ];

    // ユーザー一覧を取得
    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        try {
            const token = Cookies.get('auth_token');
            const response = await fetch(USERS_API, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const contentType = response.headers.get('content-type');
            if (!contentType || !contentType.includes('application/json')) {
                console.error('Expected JSON but got:', contentType);
                const text = await response.text();
                console.error('Response text:', text.substring(0, 200));
                return;
            }
            
            const data = await response.json();
            
            if (data.status === 'success') {
                const usersData = data.data.users || [];
                console.log('Fetched users:', usersData);
                console.log('First user:', usersData[0]);
                setUsers(usersData);
            } else {
                console.error('API error:', data.message);
            }
        } catch (err) {
            console.error('Failed to fetch users:', err);
            setError(`ユーザー取得エラー: ${err.message}`);
        }
    };

    // フィールド定義を取得
    const fetchFields = async (userId) => {
        console.log('fetchFields called with userId:', userId, 'type:', typeof userId);
        setLoading(true);
        setError('');
        try {
            console.log('Using userId directly:', userId);
            const token = Cookies.get('auth_token');
            
            const response = await fetch(`${API_BASE}?user_id=${userId}`, {
                method: 'GET',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            console.log('Main API response status:', response.status);
            
            if (!response.ok) {
                const errorText = await response.text();
                console.error('Error response text:', errorText);
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const contentType = response.headers.get('content-type');
            if (!contentType || !contentType.includes('application/json')) {
                console.error('Expected JSON but got:', contentType);
                const text = await response.text();
                console.error('Full response text:', text);
                throw new Error('サーバーからJSON形式の応答が返されていません');
            }
            
            const data = await response.json();
            if (data.status === 'success') {
                // API から返されたフィールドの options を正規化（常に配列に）
                const normalizedFields = (data.data.fields || []).map(field => {
                    let options = [];
                    if (field.options) {
                        if (typeof field.options === 'string') {
                            try {
                                options = JSON.parse(field.options);
                                if (!Array.isArray(options)) {
                                    options = [];
                                }
                            } catch (e) {
                                options = [];
                            }
                        } else if (Array.isArray(field.options)) {
                            options = field.options;
                        }
                    }
                    return { ...field, options };
                });
                setFields(normalizedFields);
                setSelectedUserId(userId);
                setNewField({
                    field_name: '',
                    field_type: 'text',
                    field_order: (normalizedFields?.length || 0) + 1,
                    is_required: false,
                    placeholder: '',
                    options: [],
                    optionsText: ''
                });
            } else {
                setError(data.message || 'フィールドの取得に失敗しました');
            }
        } catch (err) {
            setError(`通信エラー: ${err.message}`);
            console.error('Fetch error:', err);
        } finally {
            setLoading(false);
        }
    };

    // フィールドを追加
    const handleAddField = async () => {
        if (!newField.field_name) {
            setError('フィールド名を入力してください');
            return;
        }

        try {
            const token = Cookies.get('auth_token');
            // optionsText を array に変換して保存
            const optionsArray = newField.optionsText
                .split(',')
                .map(opt => opt.trim())
                .filter(Boolean);
            
            const payload = {
                user_id: selectedUserId,
                field_name: newField.field_name,
                field_type: newField.field_type,
                field_order: newField.field_order,
                is_required: newField.is_required ? 1 : 0,
                placeholder: newField.placeholder,
                options: JSON.stringify(optionsArray)  // API送信時に JSON文字列化
            };

            console.log('Sending payload:', JSON.stringify(payload, null, 2));

            const response = await fetch(API_BASE, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            console.log('Response status:', response.status);

            const contentType = response.headers.get('content-type');
            let data;
            
            if (contentType && contentType.includes('application/json')) {
                data = await response.json();
                
                console.log('Response data:', data);
            } else {
                const text = await response.text();
                console.error('Non-JSON response:', text);
                throw new Error(`サーバーエラー (${response.status}): ${text.substring(0, 200)}`);
            }

            if (response.ok && data.status === 'success') {
                setSuccess('フィールドを追加しました');
                fetchFields(selectedUserId);
                setNewField({
                    field_name: '',
                    field_type: 'text',
                    field_order: fields.length + 1,
                    is_required: false,
                    placeholder: '',
                    options: [],
                    optionsText: ''
                });
                setError('');
            } else {
                const errorMessage = data?.detail || data?.message || `HTTP ${response.status} エラー`;
                setError(`フィールド追加失敗: ${errorMessage}`);
            }
        } catch (err) {
            console.error('Error details:', err);
            setError(`エラー: ${err.message}`);
        }
    };

    // フィールドを削除
    const handleDeleteField = async (fieldId) => {
        if (!window.confirm('このフィールドを削除してもよろしいですか？')) {
            return;
        }

        try {
            const token = Cookies.get('auth_token');
            const response = await fetch(`${API_BASE}?field_id=${fieldId}&user_id=${selectedUserId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const contentType = response.headers.get('content-type');
            if (!contentType || !contentType.includes('application/json')) {
                const text = await response.text();
                console.error('Response text:', text.substring(0, 200));
                throw new Error('サーバーからJSON形式の応答が返されていません');
            }

            const data = await response.json();
            
            if (data.status === 'success') {
                setSuccess('フィールドを削除しました');
                fetchFields(selectedUserId);
            } else {
                setError(data.message || 'フィールドの削除に失敗しました');
            }
        } catch (err) {
            setError(`エラー: ${err.message}`);
            console.error('Delete error:', err);
        }
    };

    // フィールドの編集を開始
    const handleStartEdit = (field) => {
        // field.options を正規化（常に配列に）
        let options = [];
        if (field.options) {
            if (typeof field.options === 'string') {
                try {
                    options = JSON.parse(field.options);
                    if (!Array.isArray(options)) {
                        options = [];
                    }
                } catch (e) {
                    options = [];
                }
            } else if (Array.isArray(field.options)) {
                options = field.options;
            }
        }
        setEditingFieldId(field.field_id);
        setEditingField({ 
            ...field, 
            options,
            optionsText: options.join(', ')  // input 用の文字列を初期化
        });
    };

    // フィールドの編集をキャンセル
    const handleCancelEdit = () => {
        setEditingFieldId(null);
        setEditingField(null);
    };

    // フィールドを更新
    const handleSaveField = async (fieldId) => {
        if (!editingField.field_name) {
            setError('フィールド名を入力してください');
            return;
        }

        try {
            const token = Cookies.get('auth_token');
            // optionsText を array に変換して保存
            const optionsArray = editingField.optionsText
                .split(',')
                .map(opt => opt.trim())
                .filter(Boolean);
            
            const payload = {
                field_id: fieldId,
                user_id: selectedUserId,
                field_name: editingField.field_name,
                field_type: editingField.field_type,
                field_order: editingField.field_order,
                is_required: editingField.is_required ? 1 : 0,
                placeholder: editingField.placeholder,
                options: JSON.stringify(optionsArray)  // API送信時に JSON文字列化
            };

            const response = await fetch(`${API_BASE}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const contentType = response.headers.get('content-type');
            if (!contentType || !contentType.includes('application/json')) {
                const text = await response.text();
                console.error('Response text:', text.substring(0, 200));
                throw new Error('サーバーからJSON形式の応答が返されていません');
            }

            const data = await response.json();
            
            if (data.status === 'success') {
                setSuccess('フィールドを更新しました');
                setEditingFieldId(null);
                setEditingField(null);
                fetchFields(selectedUserId);
            } else {
                setError(data.message || 'フィールドの更新に失敗しました');
            }
        } catch (err) {
            setError(`エラー: ${err.message}`);
            console.error('Update error:', err);
        }
    };

    // ドラッグ開始
    const handleDragStart = (e, index) => {
        e.dataTransfer.effectAllowed = 'move';
        setDraggingIndex(index);
    };

    // ドラッグオーバー
    const handleDragOverItem = (e, index) => {
        e.preventDefault();
        setDragOverIndex(index);
    };

    // ドロップして並び替えを反映・保存
    const handleDrop = async (e, index) => {
        e.preventDefault();
        if (draggingIndex === null) return;

        const newFields = [...fields];
        const [moved] = newFields.splice(draggingIndex, 1);
        newFields.splice(index, 0, moved);

        // field_order を再計算
        const updated = newFields.map((f, idx) => ({ ...f, field_order: idx + 1 }));
        setFields(updated);
        setDraggingIndex(null);
        setDragOverIndex(null);

        // サーバーに並び順を保存（並列で送信）
        try {
            const token = Cookies.get('auth_token');
            await Promise.all(updated.map((f) => {
                const payload = {
                    field_id: f.field_id,
                    user_id: selectedUserId,
                    field_name: f.field_name,
                    field_type: f.field_type,
                    field_order: f.field_order,
                    is_required: f.is_required ? 1 : 0,
                    placeholder: f.placeholder || '',
                    options: JSON.stringify(f.options || [])
                };

                return fetch(API_BASE, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify(payload)
                }).then(res => {
                    if (!res.ok) {
                        return res.text().then(t => { throw new Error(t || `HTTP ${res.status}`); });
                    }
                    return res.json().catch(() => ({}));
                });
            }));

            setSuccess('並び順を更新しました');
            fetchFields(selectedUserId);
        } catch (err) {
            console.error('Order update error:', err);
            setError(`並び替えの保存に失敗しました: ${err.message}`);
        }
    };

    const handleDragEnd = () => {
        setDraggingIndex(null);
        setDragOverIndex(null);
    };

    const selectedUser = users.find(u => u.user_id === selectedUserId);

    return (
        <div className="min-h-screen bg-gray-100 p-8">
            <div className="max-w-6xl mx-auto">
                {/* ヘッダー */}
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900">日報フィールド設定</h1>
                    <p className="text-gray-600 mt-2">ユーザーごとに日報の提出フォーマットを設定します</p>
                </div>

                {/* メッセージ */}
                {success && (
                    <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-md">
                        <p className="text-sm text-green-800">{success}</p>
                    </div>
                )}
                {error && (
                    <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
                        <p className="text-sm text-red-800">{error}</p>
                    </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                    {/* ユーザー一覧 */}
                    <div className="lg:col-span-1">
                        <div className="bg-white rounded-lg shadow-md p-4">
                            <h2 className="text-lg font-semibold text-gray-900 mb-4">ユーザー一覧</h2>
                            <div className="space-y-2">
                                {users.map((user) => (
                                    <button
                                        key={user.user_id}
                                        onClick={() => {
                                            console.log('Button clicked for user:', user);
                                            fetchFields(user.user_id);
                                        }}
                                        className={`w-full text-left px-4 py-2 rounded-md transition-colors ${
                                            selectedUserId === user.user_id
                                                ? 'bg-blue-600 text-white'
                                                : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                                        }`}
                                    >
                                        <div className="flex items-center">
                                            <PersonIcon className="mr-2" fontSize="small" />
                                            <div>
                                                <p className="font-medium">{user.name}</p>
                                                <p className="text-xs opacity-75">ID: {user.user_id}</p>
                                            </div>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* フィールド設定 */}
                    <div className="lg:col-span-3">
                        {selectedUserId ? (
                            <div className="bg-white rounded-lg shadow-md p-6">
                                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                                    {selectedUser?.name} のフィールド設定
                                </h2>

                                {/* 既存フィールド一覧 */}
                                {fields.length > 0 && (
                                    <div className="mb-6">
                                        <h3 className="font-medium text-gray-900 mb-3">現在のフィールド</h3>
                                        <div className="space-y-2">
                                            {fields.map((field, index) => (
                                                <div
                                                    key={field.field_id}
                                                    draggable
                                                    onDragStart={(e) => handleDragStart(e, index)}
                                                    onDragOver={(e) => handleDragOverItem(e, index)}
                                                    onDrop={(e) => handleDrop(e, index)}
                                                    onDragEnd={handleDragEnd}
                                                    className={`bg-gray-50 p-4 rounded-md ${dragOverIndex === index ? 'border-2 border-dashed border-blue-300' : ''} ${draggingIndex === index ? 'opacity-50' : ''}`}
                                                >
                                                    {editingFieldId === field.field_id ? (
                                                        // 編集モード
                                                        <div className="space-y-3">
                                                            <div>
                                                                <label className="block text-sm font-medium text-gray-700 mb-1">フィールド名</label>
                                                                <input
                                                                    type="text"
                                                                    value={editingField.field_name}
                                                                    onChange={(e) => setEditingField({...editingField, field_name: e.target.value})}
                                                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                                />
                                                            </div>

                                                            <div className="grid grid-cols-2 gap-4">
                                                                <div>
                                                                    <label className="block text-sm font-medium text-gray-700 mb-1">フィールド型</label>
                                                                    <select
                                                                        value={editingField.field_type}
                                                                        onChange={(e) => setEditingField({...editingField, field_type: e.target.value})}
                                                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                                    >
                                                                        {fieldTypes.map((type) => (
                                                                            <option key={type.value} value={type.value}>
                                                                                {type.label}
                                                                            </option>
                                                                        ))}
                                                                    </select>
                                                                </div>

                                                                <div>
                                                                    <label className="block text-sm font-medium text-gray-700 mb-1">必須</label>
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={editingField.is_required}
                                                                        onChange={(e) => setEditingField({...editingField, is_required: e.target.checked})}
                                                                        className="mt-2"
                                                                    />
                                                                </div>
                                                            </div>

                                                            <div>
                                                                <label className="block text-sm font-medium text-gray-700 mb-1">プレースホルダー</label>
                                                                <input
                                                                    type="text"
                                                                    value={editingField.placeholder}
                                                                    onChange={(e) => setEditingField({...editingField, placeholder: e.target.value})}
                                                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                                />
                                                            </div>

                                                            {editingField.field_type === 'select' && (
                                                                <div>
                                                                    <label className="block text-sm font-medium text-gray-700 mb-1">選択肢（カンマで区切る）</label>
                                                                    <div className="text-xs text-gray-500 mb-2">各選択肢をカンマ（,）で区切って入力してください</div>
                                                                    <input
                                                                        type="text"
                                                                        value={editingField.optionsText}
                                                                        onChange={(e) => setEditingField({...editingField, optionsText: e.target.value})}
                                                                        placeholder="選択肢1, 選択肢2, 選択肢3"
                                                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                                                                    />
                                                                </div>
                                                            )}

                                                            <div className="flex gap-2">
                                                                <button
                                                                    onClick={() => handleSaveField(field.field_id)}
                                                                    className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors flex items-center justify-center"
                                                                >
                                                                    <SaveIcon className="mr-2" fontSize="small" />
                                                                    保存
                                                                </button>
                                                                <button
                                                                    onClick={handleCancelEdit}
                                                                    className="flex-1 px-4 py-2 bg-gray-400 text-white rounded-md hover:bg-gray-500 transition-colors flex items-center justify-center"
                                                                >
                                                                    <CancelIcon className="mr-2" fontSize="small" />
                                                                    キャンセル
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        // 表示モード
                                                        <div className="flex justify-between items-start">
                                                            <div className="flex-1">
                                                                <div className="flex items-center">
                                                                    <DragIndicatorIcon className="text-gray-400 mr-2" fontSize="small" />
                                                                    <div>
                                                                        <p className="font-medium text-gray-900">{field.field_name}</p>
                                                                        <div className="flex gap-2 mt-1">
                                                                            <span className="inline-block px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded">
                                                                                {fieldTypes.find(t => t.value === field.field_type)?.label}
                                                                            </span>
                                                                            {field.is_required && (
                                                                                <span className="inline-block px-2 py-1 bg-red-100 text-red-700 text-xs rounded">
                                                                                    必須
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                        {field.placeholder && (
                                                                            <p className="text-xs text-gray-500 mt-1">プレースホルダー: {field.placeholder}</p>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div className="flex gap-2 ml-4">
                                                                <button
                                                                    onClick={() => handleStartEdit(field)}
                                                                    className="text-blue-600 hover:text-blue-800"
                                                                >
                                                                    <EditIcon fontSize="small" />
                                                                </button>
                                                                <button
                                                                    onClick={() => handleDeleteField(field.field_id)}
                                                                    className="text-red-600 hover:text-red-800"
                                                                >
                                                                    <DeleteIcon fontSize="small" />
                                                                </button>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* 新規フィールド追加フォーム */}
                                <div className="border-t pt-6">
                                    <h3 className="font-medium text-gray-900 mb-4">新しいフィールドを追加</h3>
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">フィールド名</label>
                                            <input
                                                type="text"
                                                value={newField.field_name}
                                                onChange={(e) => setNewField({...newField, field_name: e.target.value})}
                                                placeholder="例: 訪問件数, 営業電話数"
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            />
                                        </div>

                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">フィールド型</label>
                                                <select
                                                    value={newField.field_type}
                                                    onChange={(e) => setNewField({...newField, field_type: e.target.value})}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                >
                                                    {fieldTypes.map((type) => (
                                                        <option key={type.value} value={type.value}>
                                                            {type.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">必須</label>
                                                <input
                                                    type="checkbox"
                                                    checked={newField.is_required}
                                                    onChange={(e) => setNewField({...newField, is_required: e.target.checked})}
                                                    className="mt-2"
                                                />
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">プレースホルダー（オプション）</label>
                                            <input
                                                type="text"
                                                value={newField.placeholder}
                                                onChange={(e) => setNewField({...newField, placeholder: e.target.value})}
                                                placeholder="例: 本日の訪問件数を入力してください"
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            />
                                        </div>

                                        {newField.field_type === 'select' && (
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">選択肢（カンマで区切る）</label>
                                                <div className="text-xs text-gray-500 mb-2">各選択肢をカンマ（,）で区切って入力してください</div>
                                                <input
                                                    type="text"
                                                    value={newField.optionsText}
                                                    onChange={(e) => setNewField({...newField, optionsText: e.target.value})}
                                                    placeholder="選択肢1, 選択肢2, 選択肢3"
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                                                />
                                            </div>
                                        )}

                                        <button
                                            onClick={handleAddField}
                                            disabled={loading}
                                            className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 transition-colors flex items-center justify-center"
                                        >
                                            <AddIcon className="mr-2" />
                                            フィールドを追加
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="bg-white rounded-lg shadow-md p-12 text-center text-gray-500">
                                <PersonIcon className="mx-auto text-gray-400 mb-4" style={{ fontSize: '48px' }} />
                                <p>左からユーザーを選択してください</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ReportFieldConfig;
